import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutWrapper from '../components/LayoutWrapper';
import {
  ArrowLeftIcon,
  CalendarIcon,
  ClockIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import config from '../config';
import Helper from '../helpers';

const baseUrl = config.apiBaseUrl;

const UserAttendanceReport = () => {
  const navigate = useNavigate();
  const [venueKey, setVenueKey] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const hasGeneratedReport = useRef(false);

  useEffect(() => {
    // Get venue key from token claims
    const getVenueKeyFromToken = async () => {
      const token = await Helper.getCookie('wh-jwt');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const adminVenueClaim = payload.admin_venue;
          if (adminVenueClaim) {
            setVenueKey(adminVenueClaim);
          }
        } catch (error) {
          console.error('Error parsing token:', error);
        }
      }
    };

    getVenueKeyFromToken();

    // Set default date range to last 3 months
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 6);

    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
  }, []);

  const fetchVenueUsers = useCallback(async () => {
    if (!venueKey) return;

    try {
      const token = await Helper.getCookie('wh-jwt');
      const response = await fetch(`${baseUrl}/api/users/venueusers/${venueKey}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        setError('Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Error loading users');
    }
  }, [venueKey]);

  useEffect(() => {
    fetchVenueUsers();
  }, [fetchVenueUsers]);

  const fetchAttendanceReport = useCallback(async () => {
    if (!selectedUser || !startDate || !endDate) {
      setError('Please select a user and date range');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = await Helper.getCookie('wh-jwt');
      const response = await fetch(
        `${baseUrl}/api/venues/${venueKey}/reports/user-attendance?userId=${selectedUser.userId}&startDate=${startDate}&endDate=${endDate}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
        hasGeneratedReport.current = true;
      } else {
        setError('Failed to fetch attendance report');
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Error loading report');
    } finally {
      setLoading(false);
    }
  }, [venueKey, selectedUser, startDate, endDate]);

  // Auto-refresh report when user or date range changes after initial report
  useEffect(() => {
    if (hasGeneratedReport.current && selectedUser && startDate && endDate) {
      fetchAttendanceReport();
    }
  }, [selectedUser, startDate, endDate, fetchAttendanceReport]);

  // GitHub-style contribution grid
  const contributionGrid = useMemo(() => {
    if (!reportData || !reportData.attendedSessions) return [];

    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = [];

    // Create a map of dates to session counts
    const sessionsByDate = {};
    reportData.attendedSessions.forEach(session => {
      const date = new Date(session.date).toISOString().split('T')[0];
      sessionsByDate[date] = (sessionsByDate[date] || 0) + 1;
    });

    // Generate all dates in range
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = new Date(d).toISOString().split('T')[0];
      const dateObj = new Date(d);
      days.push({
        date: dateStr,
        count: sessionsByDate[dateStr] || 0,
        dayOfWeek: dateObj.getDay(),
        month: dateObj.getMonth(),
        isFirstOfMonth: dateObj.getDate() === 1
      });
    }

    // Group by weeks
    const weeks = [];
    let currentWeek = [];

    // Pad the first week to start on Sunday
    const firstDayOfWeek = days[0]?.dayOfWeek || 0;
    for (let i = 0; i < firstDayOfWeek; i++) {
      currentWeek.push(null);
    }

    days.forEach((day) => {
      currentWeek.push(day);
      if (currentWeek.length === 7) {
        weeks.push([...currentWeek]);
        currentWeek = [];
      }
    });

    // Pad the last week
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push(currentWeek);
    }

    return weeks;
  }, [reportData, startDate, endDate]);

  // Get month labels for the grid
  const monthLabels = useMemo(() => {
    if (contributionGrid.length === 0) return [];

    const labels = [];
    contributionGrid.forEach((week, weekIndex) => {
      const firstDayWithMonth = week.find(day => day !== null);
      if (firstDayWithMonth) {
        const hasFirstOfMonth = week.some(day => day && day.isFirstOfMonth);
        if (hasFirstOfMonth || weekIndex === 0) {
          const day = week.find(d => d && d.isFirstOfMonth) || firstDayWithMonth;
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          labels.push({
            weekIndex,
            month: monthNames[day.month]
          });
        }
      }
    });

    return labels;
  }, [contributionGrid]);

  // Helper function to parse C# TimeSpan format (HH:MM:SS)
  const parseTimeSpan = (timeSpan) => {
    if (!timeSpan) return 0;

    // Handle string format "HH:MM:SS" or "D.HH:MM:SS"
    const parts = timeSpan.split(':');
    if (parts.length === 3) {
      // Check if days are included (format: "D.HH:MM:SS")
      const dayParts = parts[0].split('.');
      const days = dayParts.length === 2 ? parseInt(dayParts[0], 10) : 0;
      const hours = dayParts.length === 2 ? parseInt(dayParts[1], 10) : parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      const seconds = parseInt(parts[2], 10);

      return (days * 24 * 60) + (hours * 60) + minutes + (seconds / 60);
    }

    return 0;
  };

  const getIntensityColor = (count) => {
    if (count === 0) return 'bg-gray-800';
    if (count === 1) return 'bg-green-900';
    if (count === 2) return 'bg-green-700';
    if (count === 3) return 'bg-green-500';
    return 'bg-green-400';
  };

  return (
    <LayoutWrapper
      title="User Attendance Report"
      subtitle="Detailed attendance history and analytics"
    >
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/reports')}
          className="flex items-center text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Reports
        </button>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500 text-red-400 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="card p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Select User
            </label>
            <select
              value={selectedUser?.userId || ''}
              onChange={(e) => {
                const user = users.find(u => u.userId === e.target.value);
                setSelectedUser(user);
              }}
              className="input-field w-full"
            >
              <option value="">Choose a user...</option>
              {users.map(user => (
                <option key={user.userId} value={user.userId}>
                  {user.name || user.email}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="input-field w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="input-field w-full"
            />
          </div>
        </div>

        <div className="mt-4">
          <button
            onClick={fetchAttendanceReport}
            disabled={loading || !selectedUser}
            className="btn-primary"
          >
            {loading ? 'Loading...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {/* Report Results */}
      {reportData && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <CalendarIcon className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-300">Total Sessions</h4>
                </div>
              </div>
              <p className="text-3xl font-bold text-white">
                {reportData.attendedSessions?.length || 0}
              </p>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <CurrencyDollarIcon className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-300">Tokens Spent</h4>
                </div>
              </div>
              <div>
                <p className="text-3xl font-bold text-white mb-2">
                  {reportData.totalTokensSpentInRange || 0}
                </p>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((reportData.totalTokensSpentInRange / 100) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                    <ClockIcon className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-300">Time in Sessions</h4>
                </div>
              </div>
              <div>
                <p className="text-3xl font-bold text-white mb-2">
                  {Math.floor((reportData.timeInMinutesInSessionsInRange || 0) / 60)}h {(reportData.timeInMinutesInSessionsInRange || 0) % 60}m
                </p>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((reportData.timeInMinutesInSessionsInRange / 1000) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* GitHub-style Contribution Grid */}
          <div className="card p-6 mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">Attendance History</h3>
            <div className="overflow-x-auto">
              <div className="inline-flex flex-col gap-1">
                {/* Month labels */}
                <div className="flex gap-1 mb-1 ml-6">
                  {monthLabels.map((label, index) => (
                    <div
                      key={index}
                      className="text-xs text-gray-400"
                      style={{
                        position: 'absolute',
                        left: `${label.weekIndex * 14}px`,
                        marginLeft: '24px'
                      }}
                    >
                      {label.month}
                    </div>
                  ))}
                </div>

                {/* Day labels and grid */}
                <div className="flex gap-1" style={{ marginTop: '16px' }}>
                  {/* Day of week labels */}
                  <div className="flex flex-col gap-1 mr-1">
                    <div className="w-4 h-3 text-xs text-gray-500"></div>
                    <div className="w-4 h-3 text-xs text-gray-500 flex items-center">M</div>
                    <div className="w-4 h-3 text-xs text-gray-500"></div>
                    <div className="w-4 h-3 text-xs text-gray-500 flex items-center">W</div>
                    <div className="w-4 h-3 text-xs text-gray-500"></div>
                    <div className="w-4 h-3 text-xs text-gray-500 flex items-center">F</div>
                    <div className="w-4 h-3 text-xs text-gray-500"></div>
                  </div>

                  {/* Grid */}
                  <div className="flex gap-1">
                    {contributionGrid.map((week, weekIndex) => (
                      <div key={weekIndex} className="flex flex-col gap-1">
                        {week.map((day, dayIndex) => (
                          <div
                            key={`${weekIndex}-${dayIndex}`}
                            className={`w-3 h-3 rounded-sm ${
                              day ? getIntensityColor(day.count) : 'bg-transparent'
                            } ${day ? 'transition-all hover:ring-2 hover:ring-white cursor-pointer' : ''}`}
                            title={day ? `${day.date}: ${day.count} session${day.count !== 1 ? 's' : ''}` : ''}
                          ></div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-2 text-xs text-gray-400 ml-6">
                  <span>Less</span>
                  <div className="flex gap-1">
                    <div className="w-3 h-3 rounded-sm bg-gray-800"></div>
                    <div className="w-3 h-3 rounded-sm bg-green-900"></div>
                    <div className="w-3 h-3 rounded-sm bg-green-700"></div>
                    <div className="w-3 h-3 rounded-sm bg-green-500"></div>
                    <div className="w-3 h-3 rounded-sm bg-green-400"></div>
                  </div>
                  <span>More</span>
                </div>
              </div>
            </div>
          </div>

          {/* Session List */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Session Details</h3>
            {reportData.attendedSessions && reportData.attendedSessions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Session
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Tokens
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {reportData.attendedSessions
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((session, index) => {
                        const durationMinutes = parseTimeSpan(session.sessionLength);
                        const hours = Math.floor(durationMinutes / 60);
                        const minutes = Math.round(durationMinutes % 60);

                        return (
                          <tr key={index} className="hover:bg-gray-800/50">
                            <td className="px-4 py-3 text-sm text-gray-300">
                              {new Date(session.date).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-white">
                              {session.sessionName || 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-300">
                              {hours > 0 ? `${hours}h ` : ''}{minutes}m
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-300">
                              {session.tokenCost}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8">No sessions found in this date range</p>
            )}
          </div>
        </>
      )}

      {!reportData && !loading && (
        <div className="card p-12 text-center">
          <CalendarIcon className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">Select a user and date range to view their attendance report</p>
        </div>
      )}
    </LayoutWrapper>
  );
};

export default UserAttendanceReport;
